import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, Settings, Bell, BookOpen } from 'lucide-react';
import RastorLogo from './RastorLogo';
import { toast } from 'react-hot-toast';
import authService from '../firebase/authService';
import AuthModal from './AuthModal';
import { StatusIndicator } from './TechUI';

const Header = ({ onShowHelp, onShowOnboarding }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange(setUser);

    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      unsubscribe();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setShowUserMenu(false);
  }, [location.pathname]);

  const handleAuthSuccess = (user) => {
    setUser(user);
    setShowAuthModal(false);
    toast.success('Authentication successful');
    navigate('/profile');
  };

  const handleSignOut = async () => {
    try {
      await authService.signOut();
      setUser(null);
      setShowUserMenu(false);
      toast.success('Session terminated');
      navigate('/');
    } catch (error) {
      toast.error('Sign out failed');
    }
  };

  const openAuthModal = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  // Check if a nav link is currently active
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navLinkClass = (path) => {
    const active = isActive(path);
    return `px-4 py-2 transition-all uppercase tracking-wider font-mono text-xs ${active
      ? 'text-emerald-400 nav-link-active'
      : 'text-slate-400 hover:text-white hover:bg-white/5'
      }`;
  };

  const mobileNavLinkClass = (path) => {
    const active = isActive(path);
    return `flex items-center space-x-3 px-4 py-3 transition-colors font-mono text-xs uppercase ${active
      ? 'text-emerald-400 bg-emerald-500/10 border-l-2 border-emerald-500'
      : 'text-slate-300 hover:text-white hover:bg-white/5'
      }`;
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
          ? 'bg-[#0B0F19]/95 backdrop-blur-md border-b border-white/10 shadow-lg'
          : 'bg-transparent border-b border-white/5'
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="relative">
                <div className="w-8 h-8 border border-emerald-500/50 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                  <RastorLogo size={18} />
                </div>
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-500 animate-pulse" />
              </div>
              <span className="text-base font-mono font-bold text-white tracking-wider">
                RASTOR AI
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              <Link to="/" className={navLinkClass('/')}>
                Home
              </Link>
              <Link to="/profile" className={navLinkClass('/profile')}>
                Get Started
              </Link>
              <Link to="/resume-builder" className={navLinkClass('/resume-builder')}>
                Resume Builder
              </Link>
              <Link to="/blog" className={navLinkClass('/blog')}>
                Blog
              </Link>
              <Link to="/careers" className={navLinkClass('/careers')}>
                Careers
              </Link>
              {user && (
                <Link to="/premium-dashboard" className={navLinkClass('/premium-dashboard')}>
                  Dashboard
                </Link>
              )}
            </nav>

            {/* Desktop Auth Section */}
            <div className="hidden lg:flex items-center space-x-4">
              {/* Notification Bell */}
              {user && (
                <button className="relative p-2 text-slate-400 hover:text-white hover:bg-white/5 transition-all">
                  <Bell className="w-4 h-4" />
                  <span className="notification-dot" />
                </button>
              )}

              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white pl-2 pr-4 py-2 transition-all group font-mono text-xs"
                  >
                    {user.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt={user.displayName || 'User'}
                        className="w-6 h-6 border border-white/20"
                      />
                    ) : (
                      <div className="w-6 h-6 bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                        <User className="w-3 h-3 text-emerald-400" />
                      </div>
                    )}
                    <span className="max-w-[100px] truncate uppercase">
                      {user.displayName?.split(' ')[0] || 'USER'}
                    </span>
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-60 bg-[#0A0A0A] border border-white/10 shadow-2xl">
                      <div className="px-4 py-3 border-b border-white/10">
                        <StatusIndicator active label="AUTHENTICATED" />
                        <p className="text-xs font-mono text-white truncate mt-2">
                          {user.displayName || user.email}
                        </p>
                      </div>

                      <div className="p-2">
                        <Link
                          to="/profile"
                          className="flex items-center space-x-3 px-3 py-2 text-xs text-slate-300 hover:text-white hover:bg-white/5 transition-colors font-mono"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <Settings className="w-4 h-4" />
                          <span>SETTINGS</span>
                        </Link>

                        <button
                          onClick={handleSignOut}
                          className="flex items-center space-x-3 px-3 py-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors w-full text-left mt-1 font-mono"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>LOGOUT</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => openAuthModal('login')}
                    className="text-xs font-mono text-slate-300 hover:text-white transition-colors px-4 py-2 uppercase tracking-wider"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => openAuthModal('signup')}
                    className="flex items-center space-x-2 bg-emerald-500 text-black px-5 py-2 hover:bg-emerald-400 transition-all text-xs font-bold font-mono uppercase tracking-wider"
                  >
                    <span>Register</span>
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation — Slide-in with backdrop */}
          {isOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
                onClick={() => setIsOpen(false)}
                style={{ top: '64px' }}
              />
              <div className="lg:hidden fixed left-0 right-0 z-50 border-t border-white/10 bg-[#0A0A0A]/98 backdrop-blur-xl shadow-2xl animate-slide-up" style={{ top: '64px' }}>
                <nav className="flex flex-col p-2 max-h-[calc(100vh-64px)] overflow-y-auto">
                  <Link to="/" className={mobileNavLinkClass('/')} onClick={() => setIsOpen(false)}>
                    <span>Home</span>
                  </Link>

                  <Link to="/profile" className={mobileNavLinkClass('/profile')} onClick={() => setIsOpen(false)}>
                    <span>Get Started</span>
                  </Link>

                  {user && (
                    <Link to="/premium-dashboard" className={mobileNavLinkClass('/premium-dashboard')} onClick={() => setIsOpen(false)}>
                      <span>Dashboard</span>
                    </Link>
                  )}

                  <Link to="/resume-builder" className={mobileNavLinkClass('/resume-builder')} onClick={() => setIsOpen(false)}>
                    <span>Resume Builder</span>
                  </Link>

                  <Link to="/blog" className={mobileNavLinkClass('/blog')} onClick={() => setIsOpen(false)}>
                    <BookOpen className="w-4 h-4" />
                    <span>Blog</span>
                  </Link>

                  <Link to="/careers" className={mobileNavLinkClass('/careers')} onClick={() => setIsOpen(false)}>
                    <span>Explore Careers</span>
                  </Link>

                  <div className="h-px bg-white/10 my-2 mx-2" />

                  {user ? (
                    <>
                      <button
                        onClick={handleSignOut}
                        className="flex items-center space-x-3 px-4 py-3 text-red-400 hover:bg-red-500/10 transition-colors text-left font-mono text-xs uppercase"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </>
                  ) : (
                    <div className="p-2 grid grid-cols-2 gap-2">
                      <button
                        onClick={() => {
                          openAuthModal('login');
                          setIsOpen(false);
                        }}
                        className="px-4 py-2 text-xs font-mono text-slate-300 hover:text-white hover:bg-white/5 transition-colors text-center uppercase border border-white/10"
                      >
                        Login
                      </button>
                      <button
                        onClick={() => {
                          openAuthModal('signup');
                          setIsOpen(false);
                        }}
                        className="px-4 py-2 text-xs font-bold text-black bg-emerald-500 hover:bg-emerald-400 transition-colors text-center font-mono uppercase"
                      >
                        Register
                      </button>
                    </div>
                  )}
                </nav>
              </div>
            </>
          )}
        </div>
      </header>

      {/* Authentication Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        mode={authMode}
      />

      {/* Click outside to close user menu */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </>
  );
};

export default Header;
